// pages/Dashboard/Finances/FinancialOverview.tsx
import React from 'react';
import { DollarSign, Percent, TrendingUp, Wallet } from 'lucide-react';
import { useLanguage } from '../../../hooks/useLanguage';

interface FinancialOverviewProps {
    data: {
        revenue: {
            total: number;
            mobile: number;
            onSite: number;
            currency: string;
        };
        commission: {
            rate: number;
            totalDue: number;
            isCustomRate: boolean;
        };
        balance: {
            finalBalance: number;
            balanceType: string;
        };
        payments: {
            mobile: { count: number; net: number };
            onSite: { count: number; total: number };
        };
    };
}

export const FinancialOverview: React.FC<FinancialOverviewProps> = ({ data }) => {
    const { t, isRTL } = useLanguage();
    const { revenue, commission, balance, payments } = data;

    const cards = [
        {
            title: t('finances.overview.totalRevenue'),
            value: `${revenue.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${revenue.currency}`,
            subtitle: t('finances.overview.acceptedQuotes', { count: payments.mobile.count + payments.onSite.count }),
            icon: <DollarSign size={24} />,
            bgColor: 'bg-blue-500',
            textColor: 'text-blue-600',
            lightBg: 'bg-blue-50'
        },
        {
            title: t('finances.overview.commission'),
            value: `${commission.totalDue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${revenue.currency}`,
            subtitle: t('finances.overview.commissionRate', {
                rate: commission.rate,
                custom: commission.isCustomRate ? t('finances.overview.custom') : ''
            }),
            icon: <Percent size={24} />,
            bgColor: 'bg-purple-500',
            textColor: 'text-purple-600',
            lightBg: 'bg-purple-50'
        },
        {
            title: t('finances.overview.netEarnings'),
            value: `${(revenue.total - commission.totalDue).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${revenue.currency}`,
            subtitle: t('finances.overview.afterCommission'),
            icon: <TrendingUp size={24} />,
            bgColor: 'bg-green-500',
            textColor: 'text-green-600',
            lightBg: 'bg-green-50'
        },
        {
            title: t('finances.overview.currentBalance'),
            value: `${Math.abs(balance.finalBalance).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${revenue.currency}`,
            subtitle: balance.balanceType === 'will_receive'
                ? t('finances.overview.willReceive')
                : t('finances.overview.mustPay'),
            icon: <Wallet size={24} />,
            bgColor: balance.balanceType === 'will_receive' ? 'bg-green-500' : 'bg-red-500',
            textColor: balance.balanceType === 'will_receive' ? 'text-green-600' : 'text-red-600',
            lightBg: balance.balanceType === 'will_receive' ? 'bg-green-50' : 'bg-red-50'
        }
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {cards.map((card, index) => (
                <div
                    key={index}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 hover:shadow-md transition-shadow"
                    dir={isRTL ? 'rtl' : 'ltr'}
                >
                    <div className="flex items-center justify-between mb-2 sm:mb-4">
                        <div className={`w-8 h-8 sm:w-12 sm:h-12 ${card.lightBg} rounded-lg flex items-center justify-center`}>
                            <div className={`${card.textColor} scale-75 sm:scale-100`}>
                                {card.icon}
                            </div>
                        </div>
                    </div>
                    <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
                        {card.title}
                    </h3>
                    <p className="text-base sm:text-2xl font-bold text-gray-900 mb-1 break-words">
                        {card.value}
                    </p>
                    <p className="text-xs text-gray-500">
                        {card.subtitle}
                    </p>
                </div>
            ))}
        </div>
    );
};